import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, CalendarDays, PartyPopper, BarChart3, Settings as SettingsIcon, LogOut, Globe } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/events", label: "Events", icon: PartyPopper },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

const MOBILE_NAV = NAV.filter((n) => n.label !== "Analytics");

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-cream text-ink">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-line bg-white z-40">
        <div className="h-20 flex items-center px-6 border-b border-line">
          <img src="/logo-mark.png" alt="Drunk Fruit" className="h-10 w-auto" />
          <span className="ml-3 font-serif text-lg leading-none">Event OS</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-ink text-white" : "text-ink/70 hover:bg-clay"
                }`
              }
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <Icon className="h-[18px] w-[18px]" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-line space-y-1">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-ink/70 hover:bg-clay transition-colors">
            <Globe className="h-[18px] w-[18px]" /> View Website
          </a>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-ink/70 hover:bg-clay transition-colors" data-testid="logout-button">
            <LogOut className="h-[18px] w-[18px]" /> Sign out
          </button>
          <div className="px-4 pt-2 text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 h-16 flex items-center justify-between px-5 bg-white/90 backdrop-blur-xl border-b border-line">
        <div className="flex items-center gap-2">
          <img src="/logo-mark.png" alt="Drunk Fruit" className="h-8 w-auto" />
          <span className="font-serif text-base">Event OS</span>
        </div>
        <button onClick={doLogout} className="p-2 text-ink/60" data-testid="mobile-logout"><LogOut className="h-5 w-5" /></button>
      </header>

      {/* Content */}
      <div className="lg:pl-64">
        <div className="px-5 sm:px-8 py-6 lg:py-10 pb-28 lg:pb-10 max-w-7xl">
          <Outlet />
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-white border-t border-line flex justify-around py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {MOBILE_NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to} to={to} end={end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg text-[10px] font-medium ${isActive ? "text-ink" : "text-ink/45"}`
            }
            data-testid={`mobile-nav-${label.toLowerCase()}`}
          >
            <Icon className="h-5 w-5" /> {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
